import {
  ModalForm,
  ProFormDateTimePicker,
  ProFormSelect,
  ProFormText,
} from '@ant-design/pro-components';
import { Alert, App, Button, Flex, Form, Radio, Transfer } from 'antd';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { createCollection, loadFeishuMetadata, updateCollection } from '../services/collect';
import {
  buildFeishuCollectionValues,
  createLatestRequestGuard,
  getFeishuCollectionInitialValues,
  getFeishuEventDomainResetValues,
  getFeishuCollectionResetValues,
  getFeishuCursorFieldOptions,
  getFeishuSourceSelection,
} from '../utils/feishu-collection';

const EVENT_DOMAIN_OPTIONS = [
  { label: '计划', value: 'plan' },
  { label: '收入', value: 'income' },
];
const EVENT_VALUE_TYPE_OPTIONS = [
  { label: '金额', value: 'amount' },
  { label: '数量', value: 'quantity' },
  { label: '比率', value: 'ratio' },
];

const AgentFeishuCollectionForm = ({
  collectionType,
  connections,
  dateMappingMode,
  eventDomain: fixedEventDomain,
  onSaved,
  organizationId,
  record,
  trigger,
}) => {
  const [form] = Form.useForm();
  const metadataRequestGuardRef = useRef(createLatestRequestGuard());
  const [metadata, setMetadata] = useState({ fields: [], tables: [], views: [] });
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [metadataLoaded, setMetadataLoaded] = useState(false);
  const watchedEventDomain = Form.useWatch(['fieldMapping', 'eventDomain'], form);
  const eventDomain = fixedEventDomain || watchedEventDomain;
  const eventDateFieldId = Form.useWatch(['fieldMapping', 'eventDateFieldId'], form);
  const operationMonthFieldId = Form.useWatch(['fieldMapping', 'operationMonthFieldId'], form);
  const grossMarginPeriodFieldId = Form.useWatch(
    ['fieldMapping', 'grossMarginPeriodFieldId'],
    form,
  );
  const rentStartedAtFieldId = Form.useWatch(['fieldMapping', 'rentStartedAtFieldId'], form);
  const rentEndedAtFieldId = Form.useWatch(['fieldMapping', 'rentEndedAtFieldId'], form);
  const operationEntityCodeFieldId = Form.useWatch(
    ['fieldMapping', 'operationEntityCodeFieldId'],
    form,
  );
  const sourceStoreNameFieldId = Form.useWatch(['fieldMapping', 'sourceStoreNameFieldId'], form);
  const eventFieldIds = Form.useWatch(['fieldMapping', 'eventFieldIds'], form) || [];
  const salaryDomain = eventDomain === 'salary';
  const expenseDomain = eventDomain === 'expense';
  const grossMarginPeriodMode = dateMappingMode === 'gross-margin-period';
  const dateFieldIds = (
    grossMarginPeriodMode
      ? [operationMonthFieldId, grossMarginPeriodFieldId]
      : [eventDateFieldId, rentStartedAtFieldId, rentEndedAtFieldId]
  ).filter(Boolean);
  const targetFieldIds = [operationEntityCodeFieldId, sourceStoreNameFieldId].filter(Boolean);
  let eventDateFieldLabel = '经营日期字段';
  if (eventDomain === 'plan') {
    eventDateFieldLabel = '计划月份字段';
  } else if (salaryDomain) {
    eventDateFieldLabel = '工资月份字段';
  } else if (expenseDomain) {
    eventDateFieldLabel = '费用日期字段';
  }
  let eventFieldLabel = '经营事件字段';
  let selectedEventFieldLabel = '已选经营事件字段';
  if (salaryDomain) {
    eventFieldLabel = '工资字段';
    selectedEventFieldLabel = '已选工资字段';
  } else if (expenseDomain) {
    eventFieldLabel = '费用金额字段';
    selectedEventFieldLabel = '已选费用金额字段';
  }
  const { message } = App.useApp();
  const editing = Boolean(record);

  const resetSourceMetadata = () => {
    metadataRequestGuardRef.current.invalidate();
    setMetadata({ fields: [], tables: [], views: [] });
    setLoadingMetadata(false);
    setMetadataLoaded(false);
    form.setFieldsValue(
      getFeishuCollectionResetValues({
        appToken: undefined,
        tableId: undefined,
        viewId: undefined,
      }),
    );
  };

  const loadMetadata = async (options = {}) => {
    const connectionId = options.connectionId || form.getFieldValue('connectionId');
    const sourceReference = (
      options.sourceReference || form.getFieldValue('sourceReference')
    )?.trim();
    if (!connectionId || !sourceReference) {
      throw new Error('请先选择连接并输入多维表格链接');
    }
    const sourceSelection = options.applySourceSelection
      ? getFeishuSourceSelection(sourceReference)
      : {};
    const tableId = options.tableId || sourceSelection.tableId;
    const viewId = options.viewId || sourceSelection.viewId;
    const requestId = metadataRequestGuardRef.current.start();
    setLoadingMetadata(true);
    setMetadataLoaded(false);
    try {
      const result = await loadFeishuMetadata({
        connectionId,
        organizationId,
        ...(sourceReference.startsWith('http')
          ? { sourceUrl: sourceReference }
          : { appToken: sourceReference }),
        ...(tableId ? { tableId } : {}),
      });
      if (!metadataRequestGuardRef.current.isLatest(requestId)) {
        return undefined;
      }
      setMetadata(result);
      setMetadataLoaded(true);
      form.setFieldsValue({
        sourceConfig: {
          appToken: result.app.appToken,
          ...(tableId ? { tableId } : {}),
          ...(viewId ? { viewId } : {}),
        },
      });
      return result;
    } catch (error) {
      if (metadataRequestGuardRef.current.isLatest(requestId)) {
        throw error;
      }
      return undefined;
    } finally {
      if (metadataRequestGuardRef.current.isLatest(requestId)) {
        setLoadingMetadata(false);
      }
    }
  };

  const fieldOptions = metadata.fields.map(({ fieldId, name }) => ({
    label: name,
    value: fieldId,
  }));
  const cursorFieldOptions = getFeishuCursorFieldOptions(metadata.fields);
  const fieldNameById = Object.fromEntries(
    metadata.fields.map(({ fieldId, name }) => [fieldId, name]),
  );
  const transferDataSource = metadata.fields.map(({ fieldId, name }) => ({
    key: fieldId,
    title: name,
    disabled:
      (dateFieldIds.includes(fieldId) || targetFieldIds.includes(fieldId)) &&
      !eventFieldIds.includes(fieldId),
  }));

  return (
    <ModalForm
      form={form}
      title={editing ? '修改采集配置' : '创建采集配置'}
      trigger={trigger}
      width={760}
      initialValues={getFeishuCollectionInitialValues(record, fixedEventDomain)}
      modalProps={{ destroyOnHidden: true }}
      onOpenChange={(open) => {
        if (!open) {
          metadataRequestGuardRef.current.invalidate();
          setMetadata({ fields: [], tables: [], views: [] });
          setLoadingMetadata(false);
          setMetadataLoaded(false);
          return;
        }
        if (record) {
          loadMetadata({
            connectionId: record.connectionId,
            sourceReference: record.sourceConfig.sourceUrl || record.sourceConfig.appToken,
            tableId: record.sourceConfig.tableId,
            viewId: record.sourceConfig.viewId,
          }).catch((error) => message.error(error.message));
        }
      }}
      submitter={{ submitButtonProps: { disabled: loadingMetadata || !metadataLoaded } }}
      onFinish={async (values) => {
        try {
          const collectionValues = buildFeishuCollectionValues(
            values,
            metadata,
            fixedEventDomain,
            dateMappingMode,
          );
          await (editing ? updateCollection : createCollection)({
            data: { ...collectionValues, collectionType, id: record?.id, organizationId },
          });
          onSaved();
          return true;
        } catch (error) {
          message.error(error.message);
          return false;
        }
      }}
    >
      {expenseDomain ? (
        <Alert
          showIcon
          type="info"
          title="飞书费用会与合思费用共同计入；请仅配置合思未采集的数据，避免重复。"
        />
      ) : null}
      <ProFormText name="name" label="采集配置名称" rules={[{ required: true }]} />
      <ProFormSelect
        name="connectionId"
        label="飞书连接"
        options={connections.map(({ id, name }) => ({ label: name, value: id }))}
        fieldProps={{ onChange: resetSourceMetadata }}
        rules={[{ required: true }]}
      />
      <ProFormText
        name="sourceReference"
        label="多维表格链接"
        rules={[{ required: true }]}
        fieldProps={{
          onChange: resetSourceMetadata,
          addonAfter: (
            <Button
              type="text"
              loading={loadingMetadata}
              onClick={() =>
                loadMetadata({ applySourceSelection: true }).catch((error) =>
                  message.error(error.message),
                )
              }
            >
              读取
            </Button>
          ),
        }}
      />
      <ProFormText name={['sourceConfig', 'appToken']} hidden />
      <ProFormSelect
        name={['sourceConfig', 'tableId']}
        label="数据表"
        options={metadata.tables.map(({ tableId, name }) => ({ label: name, value: tableId }))}
        fieldProps={{
          onChange: (tableId) => {
            form.setFieldsValue(getFeishuCollectionResetValues({ viewId: undefined }));
            setMetadata((current) => ({ ...current, fields: [], views: [] }));
            setMetadataLoaded(false);
            loadMetadata({ tableId }).catch((error) => message.error(error.message));
          },
        }}
        rules={[{ required: true }]}
      />
      <ProFormSelect
        name={['sourceConfig', 'viewId']}
        label="视图"
        options={metadata.views.map(({ viewId, name }) => ({ label: name, value: viewId }))}
        rules={[{ required: true }]}
      />
      {fixedEventDomain ? null : (
        <ProFormSelect
          name={['fieldMapping', 'eventDomain']}
          label="经营事件子领域"
          options={EVENT_DOMAIN_OPTIONS}
          fieldProps={{
            onChange: (value) => form.setFieldsValue(getFeishuEventDomainResetValues(value)),
          }}
          rules={[{ required: true }]}
        />
      )}
      {expenseDomain ? null : (
        <Form.Item
          name={['sourceConfig', 'collectionMode']}
          label="采集模式"
          extra="全量采集每次从配置的采集起始时间重新读取，不使用上次采集水位作为起点；起始时间之前的记录不采集。"
          rules={[{ required: true, message: '请选择采集模式' }]}
        >
          <Radio.Group
            options={[
              { label: '增量采集', value: 'incremental' },
              { label: '全量采集', value: 'full' },
            ]}
          />
        </Form.Item>
      )}
      <ProFormDateTimePicker
        name={['sourceConfig', 'startTime']}
        label="采集起始时间"
        rules={[{ required: true }]}
      />
      {eventDomain === 'plan' ? null : (
        <ProFormSelect
          name={['fieldMapping', 'sourceStoreNameFieldId']}
          label="来源门店名称字段"
          options={fieldOptions}
          rules={[{ required: true }]}
        />
      )}
      <ProFormSelect
        name={['fieldMapping', 'operationEntityCodeFieldId']}
        label="经营实体编码字段"
        options={fieldOptions}
        rules={[{ required: true }]}
      />
      {grossMarginPeriodMode ? (
        <>
          <ProFormSelect
            name={['fieldMapping', 'operationMonthFieldId']}
            label="经营月份字段"
            options={fieldOptions}
            rules={[{ required: true }]}
          />
          <ProFormSelect
            name={['fieldMapping', 'grossMarginPeriodFieldId']}
            label="毛利率时段字段"
            options={fieldOptions}
            rules={[{ required: true }]}
          />
        </>
      ) : (
        <ProFormSelect
          name={['fieldMapping', 'eventDateFieldId']}
          label={eventDateFieldLabel}
          options={fieldOptions}
          rules={[{ required: true }]}
        />
      )}
      {expenseDomain ? (
        <>
          <ProFormSelect
            name={['fieldMapping', 'rentStartedAtFieldId']}
            label="费用适用开始日期字段（可选）"
            options={fieldOptions}
          />
          <ProFormSelect
            name={['fieldMapping', 'rentEndedAtFieldId']}
            label="费用适用结束日期字段（可选）"
            options={fieldOptions}
          />
        </>
      ) : null}
      <ProFormSelect
        name={['sourceConfig', 'cursorFieldId']}
        label="最后更新时间字段"
        options={cursorFieldOptions}
        rules={[{ required: true }]}
      />
      <Form.Item
        name={['fieldMapping', 'eventFieldIds']}
        label={eventFieldLabel}
        valuePropName="targetKeys"
        rules={[{ required: true, type: 'array', min: 1, message: '请至少选择一个经营事件字段' }]}
      >
        <Transfer
          dataSource={transferDataSource}
          style={{ width: '100%' }}
          styles={{ section: { flex: '1 1 0', minWidth: 0 } }}
          titles={['可选字段', selectedEventFieldLabel]}
          showSearch
          render={(item) => item.title}
        />
      </Form.Item>
      {eventFieldIds.length > 0 && !expenseDomain ? (
        <Flex vertical>
          {eventFieldIds.map((fieldId) => (
            <ProFormSelect
              key={fieldId}
              name={['fieldMapping', 'eventValueTypes', fieldId]}
              label={`${fieldNameById[fieldId] || fieldId}数值类型`}
              options={EVENT_VALUE_TYPE_OPTIONS}
              rules={[{ required: true, message: '请选择数值类型' }]}
            />
          ))}
        </Flex>
      ) : null}
    </ModalForm>
  );
};

AgentFeishuCollectionForm.propTypes = {
  collectionType: PropTypes.string.isRequired,
  connections: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string.isRequired }),
  ).isRequired,
  dateMappingMode: PropTypes.oneOf(['gross-margin-period']),
  onSaved: PropTypes.func.isRequired,
  eventDomain: PropTypes.oneOf(['expense', 'income', 'salary']),
  organizationId: PropTypes.string.isRequired,
  record: PropTypes.shape({
    connectionId: PropTypes.string.isRequired,
    fieldMapping: PropTypes.shape({
      eventDateFieldId: PropTypes.string,
      operationMonthFieldId: PropTypes.string,
      grossMarginPeriodFieldId: PropTypes.string,
      rentStartedAtFieldId: PropTypes.string,
      rentEndedAtFieldId: PropTypes.string,
      eventDomain: PropTypes.oneOf(['expense', 'income', 'plan', 'salary']),
      events: PropTypes.arrayOf(
        PropTypes.shape({
          fieldId: PropTypes.string,
          typeLevel1: PropTypes.string,
          typeLevel2: PropTypes.string,
          typeLevel3: PropTypes.string,
          valueType: PropTypes.oneOf(['amount', 'quantity', 'ratio']),
        }),
      ),
      operationEntityCodeFieldId: PropTypes.string,
      sourceStoreNameFieldId: PropTypes.string,
    }),
    id: PropTypes.string,
    name: PropTypes.string,
    sourceConfig: PropTypes.shape({
      collectionMode: PropTypes.oneOf(['incremental', 'full']),
      sourceUrl: PropTypes.string,
      appToken: PropTypes.string.isRequired,
      cursorFieldId: PropTypes.string.isRequired,
      tableId: PropTypes.string.isRequired,
      viewId: PropTypes.string,
      startTime: PropTypes.number.isRequired,
    }),
  }),
  trigger: PropTypes.node.isRequired,
};

export default AgentFeishuCollectionForm;
